/**
 * Minimal async-data hook (no react-query dependency).
 *
 *   const { data, loading, error, reload } = useAsync(() => client.listInvitations(status), [status]);
 *
 * Re-runs when `deps` change; `reload()` forces a re-fetch (e.g. after a mutation).
 * Guards against setState-after-unmount and out-of-order responses.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  reload: () => void;
}

export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    fnRef
      .current()
      .then((result) => {
        if (active) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (active) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return { data, loading, error, reload };
}

/** Format an OxboAuthError (or any error) for display. */
export function errorMessage(err: unknown): string {
  if (err && typeof err === "object" && "status" in err) {
    const e = err as { status?: number; body?: unknown };
    const detail =
      e.body && typeof e.body === "object" && "detail" in e.body
        ? String((e.body as { detail: unknown }).detail)
        : typeof e.body === "string"
          ? e.body
          : "";
    return `Request failed (${e.status ?? "?"})${detail ? `: ${detail}` : ""}`;
  }
  return err instanceof Error ? err.message : String(err);
}
